import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, MailCheck, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_workspace-pro-29/artifacts/n0cixeqd_Logo_aesthetic_bianco_blu_piccolo.png";
const GOOGLE_ICON = "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg";

export default function Login() {
  const { login, verifyOtp, recoveryLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("aesthetic-os-remember-email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem("aesthetic-os-remember-email"));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  // 2FA OTP
  const [step, setStep] = useState("credentials"); // credentials | otp | recovery
  const [recoveryCode, setRecoveryCode] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState(null); // null = step email; "google" | "password"
  const [checking, setChecking] = useState(false);

  const checkEmail = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Inserisci la tua email"); return; }
    if (remember) localStorage.setItem("aesthetic-os-remember-email", email);
    else localStorage.removeItem("aesthetic-os-remember-email");
    setChecking(true);
    try {
      const { data } = await api.post("/auth/login/method", { email: email.trim().toLowerCase() });
      setAuthMethod(data.method === "google" ? "google" : "password");
    } catch {
      setAuthMethod("password");
    } finally { setChecking(false); }
  };
  const resetEmailStep = () => { setAuthMethod(null); setPassword(""); setError(""); };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (remember) localStorage.setItem("aesthetic-os-remember-email", email);
    else localStorage.removeItem("aesthetic-os-remember-email");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { navigate(res.user?.role === "client_user" ? "/portal" : "/"); return; }
    if (res.otpRequired) {
      setPendingToken(res.pendingToken); setEmailHint(res.emailHint || "");
      setCooldown(res.resendIn || 60); setStep("otp"); setCode("");
      return;
    }
    setError(res.error);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await verifyOtp(pendingToken, code.trim());
    setLoading(false);
    if (res.ok) navigate(res.user?.role === "client_user" ? "/portal" : "/");
    else setError(res.error);
  };

  const submitRecovery = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await recoveryLogin(email, password, recoveryCode.trim());
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      const { data } = await api.post("/auth/resend-otp", { pending_token: pendingToken });
      setPendingToken(data.pending_token); setCooldown(data.resend_in || 60); setError("");
      toast.success("Nuovo codice inviato.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossibile inviare un nuovo codice.");
    }
  };

  const googleLogin = async () => {
    setGoogleLoading(true); setError("");
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const { data } = await api.get("/auth/google/url", { params: { redirect_uri: window.location.origin + "/auth/google" } });
      window.location.href = data.url;
    } catch (err) {
      setGoogleLoading(false);
      setError(err?.response?.data?.detail || "Login con Google non disponibile.");
    }
  };

  const sendReset = async () => {
    if (!forgotEmail.trim()) return toast.error("Inserisci la tua email");
    try {
      await api.post(`/auth/forgot-password`, { email: forgotEmail.trim() });
      toast.success("Se l'email è registrata, riceverai le istruzioni per reimpostare la password.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch {
      toast.error("Invio non riuscito. Riprova tra qualche minuto.");
    }
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden border-r border-border/60 bg-card p-12 lg:flex">
        <div />

        <div className="max-w-md space-y-5">
          <img src={BRAND_LOGO} alt="Aesthetic Digital Studio" className="-mt-[68px] mb-2 w-[202px] object-contain" data-testid="login-brand-logo" />
          <h1 className="font-display text-4xl font-semibold leading-[1.1] tracking-tight">
            Sistema Operativo
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground">
            Campagne, attività e risultati in un unico spazio condiviso tra te e la
            tua agenzia — sempre allineati.
          </p>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px w-8 bg-border" />
          Aesthetic Digital Studio Srl
        </div>

        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-foreground/[0.03] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-12 h-72 w-72 rounded-full bg-foreground/[0.03] blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-1/2">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-3 flex items-center">
            <img src="/logo-ae.png" alt="AE Agency System" className="h-10 w-auto object-contain" data-testid="login-ae-logo" />
          </div>

          <h2 className="font-display text-2xl font-semibold tracking-tight"><span>{step === "otp" ? "Controlla la tua email" : step === "recovery" ? "Recupero Owner" : "Bentornato"}</span></h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {step === "otp" ? <>Abbiamo inviato un codice a 6 cifre a <b>{emailHint}</b>.</>
              : step === "recovery" ? "Accesso di emergenza con codice di recupero monouso (solo Owner)."
              : "Accedi al tuo spazio di lavoro."}
          </p>

          {step === "recovery" ? (
            <form onSubmit={submitRecovery} className="mt-8 space-y-4" data-testid="recovery-form">
              <div className="space-y-1.5">
                <Label htmlFor="rec-email">Email</Label>
                <Input id="rec-email" type="email" data-testid="recovery-email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rec-password">Password</Label>
                <Input id="rec-password" type="password" data-testid="recovery-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rec-code">Codice di recupero</Label>
                <Input id="rec-code" data-testid="recovery-code" placeholder="XXXX-XXXX-XXXX" value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())} autoComplete="off" />
              </div>
              {error && <p data-testid="recovery-error" className="text-sm font-medium text-red-500">{error}</p>}
              <Button type="submit" data-testid="recovery-submit" disabled={loading || !email || !password || recoveryCode.length < 8} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="inline-flex items-center"><ShieldCheck className="mr-1.5 h-4 w-4" /><span>Accedi con codice di recupero</span></span>}
              </Button>
              <button type="button" data-testid="recovery-back" onClick={() => { setStep("credentials"); setError(""); }}
                className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">Torna al login</button>
            </form>
          ) : step === "otp" ? (
            <form onSubmit={submitOtp} className="mt-8 space-y-4" data-testid="otp-form">
              <div className="space-y-1.5">
                <Label htmlFor="otp-code">Codice di verifica</Label>
                <Input id="otp-code" data-testid="otp-code" inputMode="numeric" autoComplete="one-time-code"
                  maxLength={6} placeholder="000000" value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center text-lg tracking-[0.4em]" autoFocus />
              </div>
              {error && <p data-testid="otp-error" className="text-sm font-medium text-red-500">{error}</p>}
              <Button type="submit" data-testid="otp-submit" disabled={loading || code.length < 6} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="inline-flex items-center"><ShieldCheck className="mr-1.5 h-4 w-4" /><span>Verifica e accedi</span></span>}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" data-testid="otp-back" onClick={() => { setStep("credentials"); setError(""); }}
                  className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">Torna al login</button>
                <button type="button" data-testid="otp-resend" onClick={resend} disabled={cooldown > 0}
                  className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50">
                  <span>{cooldown > 0 ? `Rinvia tra ${cooldown}s` : "Invia nuovamente il codice"}</span>
                </button>
              </div>
            </form>
          ) : (
          <>
          {authMethod === null && (
            <form onSubmit={checkEmail} className="mt-8 space-y-4" data-testid="login-email-step">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="login-email" placeholder="nome@azienda.it"
                  value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoFocus />
              </div>
              {error && <p data-testid="login-error" className="text-sm font-medium text-red-500">{error}</p>}
              <Button type="submit" data-testid="login-continue" disabled={checking} className="w-full">
                {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="inline-flex items-center"><span>Continua</span><ArrowRight className="ml-1 h-4 w-4" /></span>}
              </Button>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  data-testid="remember-me" className="h-4 w-4 rounded border-border accent-foreground" />
                Ricorda i miei dati d'accesso
              </label>
            </form>
          )}

          {authMethod === "google" && (
            <div className="mt-8 space-y-4" data-testid="login-google-step">
              <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm">
                <span className="truncate text-foreground">{email}</span>
                <button type="button" onClick={resetEmailStep} data-testid="login-change-email"
                  className="shrink-0 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">Cambia</button>
              </div>
              <Button type="button" variant="outline" onClick={googleLogin} disabled={googleLoading}
                data-testid="login-google" className="w-full gap-2">
                {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <img src={GOOGLE_ICON} alt="" className="h-4 w-4" />}
                <span>Accedi con Google</span>
              </Button>
              <div className="relative py-1 text-center">
                <span className="relative z-10 bg-background px-2 text-xs uppercase tracking-wider text-muted-foreground">oppure</span>
                <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password-g">Password</Label>
                  <div className="relative">
                    <Input id="password-g" type={showPassword ? "text" : "password"} data-testid="login-password"
                      placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password" className="pr-10" />
                    <button type="button" onClick={() => setShowPassword((s) => !s)} tabIndex={-1}
                      data-testid="login-password-toggle"
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
                      title={showPassword ? "Nascondi password" : "Mostra password"}>
                      <span className="inline-flex">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</span>
                    </button>
                  </div>
                </div>
                {error && <p data-testid="login-error" className="text-sm font-medium text-red-500">{error}</p>}
                <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="inline-flex items-center"><span>Accedi con password</span><ArrowRight className="ml-1 h-4 w-4" /></span>}
                </Button>
                <div className="flex items-center justify-end">
                  <button type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)}
                    className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline">Password dimenticata?</button>
                </div>
              </form>
            </div>
          )}

          {authMethod === "password" && (
          <>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm">
              <span className="truncate text-foreground">{email}</span>
              <button type="button" onClick={resetEmailStep} data-testid="login-change-email"
                className="shrink-0 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">Cambia</button>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="pr-10"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
                  title={showPassword ? "Nascondi password" : "Mostra password"}
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  data-testid="login-password-toggle"
                  tabIndex={-1}
                >
                  <span className="inline-flex">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</span>
                </button>
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm font-medium text-red-500">
                {error}
              </p>
            )}

            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="inline-flex items-center">
                  <span>Accedi</span>
                  <ArrowRight className="ml-1 h-4 w-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => setForgotOpen(true)}
              className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              Password dimenticata?
            </button>
          </div>
          </>
          )}
          </>
          )}
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-sm" data-testid="forgot-password-modal">
          <DialogHeader>
            <DialogTitle>Recupera l'accesso</DialogTitle>
            <DialogDescription>
              Inserisci l'email del tuo account. Ti invieremo le istruzioni per accedere di nuovo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="forgot-email">Email</Label>
            <Input id="forgot-email" type="email" data-testid="forgot-email" placeholder="nome@esempio.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotOpen(false)}>Annulla</Button>
            <Button onClick={sendReset} data-testid="forgot-send"><MailCheck className="mr-1.5 h-4 w-4" /> Invia istruzioni</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
